import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ownerAvatarPath} from '@github-ui/paths'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {RepoIcon} from '@primer/octicons-react'
import {Label, Link, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {FC} from 'react'

import {LABELS} from '../../constants/labels'
import type {RepositoryPillRepositoryNode, RepositoryPillRepositoryOwnerNode} from './repository-pill-query-types'
import styles from './RepositoryPill.module.css'

// RELAY-CLEANUP(HeaderMetadata, StickyHeaderChips): widens nullable fields so the still-Relay
// parents' data is assignable. Remove when HeaderMetadata and StickyHeaderChips leave Relay.
type RepositoryPillFragment = RelayCompat<{
  readonly repository: Pick<RepositoryPillRepositoryNode, 'name' | 'isPrivate'> & {
    // `name` is the owner's display name, used for the account pill; optional so consumers that
    // don't select it still type-check.
    readonly owner: Pick<RepositoryPillRepositoryOwnerNode, 'login' | '__typename'> & {
      readonly name?: string | null
    }
  }
}>

export type RepositoryPillProps = {
  repositoryPillData: RepositoryPillFragment
  className?: string
  isSmall?: boolean
  /** When true, the account owner is surfaced instead of the repository (account-issue surfaces only). */
  isAccountIssue?: boolean
}

export const SmallRepositoryPill: FC<RepositoryPillProps> = props => <RepositoryPill {...props} isSmall />

export function RepositoryPill({
  repositoryPillData,
  className,
  isSmall = false,
  isAccountIssue = false,
}: RepositoryPillProps) {
  const {repository} = repositoryPillData
  const repoOwner = repository.owner.login
  const repoName = repository.name
  // Account issues surface the owning account (organization or user) instead of the backing
  // repository. Repository-scoped viewers keep the repository pill, so this is gated on the
  // account-issue signal in addition to the flag; `__typename` also selects the hovercard type
  // and drives the avatar shape below.
  const isOrganization = repository.owner.__typename === 'Organization'
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = isOrganization
    ? getHovercardAttributesForType('organization', {login: repoOwner})
    : getHovercardAttributesForType('user', {login: repoOwner})

  const showAccountOwner = isAccountIssue && isFeatureEnabled('planning_account_issue_icon')

  if (showAccountOwner) {
    // Prefer the account's display name, matching the account-issue switcher; the login is the
    // fallback when the display name isn't set.
    const accountName = repository.owner.name ?? repoOwner
    // Account issues live under the account's planning tab, so link there (the issue's real home)
    // rather than the account profile. `orgs`/`users` mirrors the planning route's accountType.
    const accountHref = `/${isOrganization ? 'orgs' : 'users'}/${repoOwner}/planning/issues`
    const account = (
      <div className={clsx(styles.repositoryInfoContainer, !isSmall && styles.repositoryInfoContainerLarge)}>
        {/* Organization avatars are square; user avatars are round, matching the account issue list. */}
        <VisuallyHidden>{`${isOrganization ? LABELS.organization : LABELS.user}: `}</VisuallyHidden>
        <GitHubAvatar
          src={ownerAvatarPath({owner: repoOwner})}
          size={16}
          square={isOrganization}
          alt=""
          className={styles.accountOwnerAvatar}
          data-testid="account-owner-avatar"
        />
        <Link href={accountHref} {...hovercardAttrs} className={styles.ownerLink}>
          {accountName}
        </Link>
      </div>
    )

    if (isSmall) {
      return account
    }

    return <div className={clsx(styles.smallRepositoryPill, className)}>{account}</div>
  }

  const link = (
    <div className={clsx(styles.repositoryInfoContainer, !isSmall && styles.repositoryInfoContainerLarge)}>
      {!isSmall && <RepoIcon className={styles.repositoryIcon} />} {/* 2px to achieve visual alignment */}
      <Link href={`/${repoOwner}`} {...hovercardAttrs} className={styles.ownerLink}>
        {repoOwner}
      </Link>
      <span>/</span>
      <Link href={`/${repoOwner}/${repoName}`} className={styles.repositoryNameLink}>
        {repoName}
      </Link>
    </div>
  )

  if (isSmall) {
    return link
  }

  return (
    <div className={clsx(styles.smallRepositoryPill, className)}>
      {link}
      <Label size="small" data-watch-overflow className={styles.repositoryLabel}>
        {repository.isPrivate ? LABELS.private : LABELS.public}
      </Label>
    </div>
  )
}
